import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  facilityLabel,
  findOneAchc,
  formatSapDateTime,
  getDocuments,
  getLoggedInUser,
  getMaritalLabel,
  getRemarksList,
  fetchSpouseDetails,
  getStatusBadgeClass,
  getStatusLabel,
  normalizeDobToDMY,
} from "./authorityLetterHealthCheckup.service";

const NO_DATA = "-";

const DisplayAuthorityLetter: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { referenceNumber } = useParams<{ referenceNumber: string }>();
  const user = getLoggedInUser();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [profileSpouse, setProfileSpouse] = useState({ spouseName: "", spouseDob: "" });

  const refId = referenceNumber ? decodeURIComponent(referenceNumber) : "";

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneAchc(refId);
        setDetail(data);
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || t("failed_to_load_details"));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [refId, t]);

  useEffect(() => {
    let cancelled = false;
    fetchSpouseDetails()
      .then((spouse) => {
        if (!cancelled) setProfileSpouse(spouse);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const comprehensiveFor =
    detail?.ComprehensiveCheckupFor ??
    detail?.comprehensiveCheckupFor ??
    detail?.facilityRequiredFor ??
    "";
  const showSpouseFields =
    comprehensiveFor === "S" ||
    comprehensiveFor === "B" ||
    comprehensiveFor === "SPOUSE" ||
    comprehensiveFor === "BOTH";
  const empDob = user?.details?.birthdate ?? detail?.EmployeeDob ?? detail?.DateOfBirthOfEmployee;
  const spouseName = detail?.spouseName ?? detail?.SpouseName ?? profileSpouse.spouseName;
  const spouseDob = detail?.spouseDob ?? detail?.SpouseDob ?? profileSpouse.spouseDob;
  const remarks = getRemarksList(detail);
  const documents = getDocuments(detail);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">
              {t("authority_letter_for_annual_comprehensive_health_checkup")}
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.authorityLetterHealthCheckup.list}>
                    {t("authority_letter_for_annual_comprehensive_health_checkup")}
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {t("display_request")}
                </li>
              </ol>
            </nav>
          </div>
          <button className="btn btn-light" onClick={() => navigate(-1)}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("close")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="alert alert-light border">{t("no_details_available")}</div>
        ) : (
          <>
            <div className="alert alert-secondary d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{t("request_status")}</span>
              <span className={getStatusBadgeClass(detail?.RequestStatus)}>
                {getStatusLabel(detail?.RequestStatus, t)}
              </span>
            </div>

            <Section title={t("request_information")} icon="ti ti-briefcase">
              <Field label={t("reference_number") || t("reference_no")} value={detail?.ReferenceNumber || NO_DATA} />
              <Field label={t("request_type")} value={t("annual_comprehensive_medical")} />
              <Field
                label={t("created_at")}
                value={detail?.RequestCreationDate || NO_DATA}
              />
              <Field label={t("employee_id")} value={detail?.EmpID || NO_DATA} />
            </Section>

            <Section title={t("employee_details")} icon="ti ti-user">
              <Field label={t("phone_number")} value={detail?.ContactNumber || NO_DATA} />
              <Field label={t("email_id")} value={detail?.EmailID || NO_DATA} />
            </Section>

            <Section title={t("checkup_details")} icon="ti ti-stethoscope">
              <Field label={t("application_for")} value={t("annual_comprehensive_medical")} />
              <Field label={t("facility_required_for")} value={facilityLabel(comprehensiveFor)} />
              <Field label={t("marital_status")} value={getMaritalLabel(detail?.MaritalStatus)} />
              <Field
                label={t("from_hospital")}
                value={detail?.HospitalName || detail?.HospitalRecordKey || NO_DATA}
              />
              <Field
                label={t("dob_employee")}
                value={empDob ? normalizeDobToDMY(empDob) : NO_DATA}
              />
              {showSpouseFields && (
                <>
                  <Field label={t("spouse_name")} value={spouseName || NO_DATA} />
                  <Field
                    label={t("spouse_dob")}
                    value={spouseDob ? normalizeDobToDMY(spouseDob) : NO_DATA}
                  />
                </>
              )}
            </Section>

            {documents.length > 0 && (
              <Section title={t("attachedFiles")} icon="ti ti-paperclip">
                {documents.map((doc: any, index: number) => {
                  const url = doc?.url || doc?.URL || doc?.fileUrl;
                  const fileName = doc.FileName || doc.fileName || `Document_${index + 1}.pdf`;
                  return (
                    <div key={doc.id || index} className="border rounded p-2 mb-2">
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </a>
                      ) : (
                        <span className="fw-semibold">
                          <i className="ti ti-file me-1"></i>
                          {fileName}
                        </span>
                      )}
                    </div>
                  );
                })}
              </Section>
            )}

            {remarks.length > 0 && (
              <Section title={t("History")} icon="ti ti-history">
                {remarks.map((remark: any, index: number) => (
                  <div
                    key={index}
                    className={`py-2 ${index < remarks.length - 1 ? "border-bottom" : ""}`}
                  >
                    <div className="fw-semibold">
                      {remark?.approverName ?? remark?.ApproverName ?? NO_DATA}
                      {(remark?.approverUserID || remark?.ApproverUserID) && (
                        <small className="text-muted ms-2">
                          ({remark?.approverUserID ?? remark?.ApproverUserID})
                        </small>
                      )}
                    </div>
                    <div className="small text-muted">
                      {formatSapDateTime(remark?.createdAt ?? remark?.TimeStamp)}
                    </div>
                    <div>{remark?.remarks ?? remark?.Remarks ?? remark?.HRemarks ?? NO_DATA}</div>
                  </div>
                ))}
              </Section>
            )}

            <Section title={t("Declaration")} icon="ti ti-checkbox">
              <p className="mb-1">1. {t("declaration_line1")}</p>
              <p className="mb-1">2. {t("declaration_line2")}</p>
            </Section>

            <div className="alert alert-success d-flex align-items-center">
              <i className="ti ti-info-circle me-2 fs-18"></i>
              {t("readonly_notice")}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <div className="card border-0 shadow-sm mb-3">
    <div className="card-header bg-white">
      <h5 className="mb-0 fw-bold">
        <i className={`${icon} me-2`}></i>
        {title}
      </h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const Field: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? "-"}</span>
  </div>
);

export default DisplayAuthorityLetter;
